import { useCallback, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { SupabaseClient } from "@supabase/supabase-js"
import { toast } from "sonner"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/hooks/useAuth"
import {
  conectarWhatsApp,
  desconectarWhatsApp,
  enviarWhatsApp,
  statusWhatsApp,
  type WhatsAppConnection,
} from "@/config/whatsapp"

// As tabelas whatsapp_instances / whatsapp_messages são novas: os tipos gerados
// do Supabase (types.ts) ainda não as conhecem. Assim que rodar a migration e
// regenerar os tipos, basta trocar `db` por `supabase`.
const db = supabase as unknown as SupabaseClient

/**
 * Conexão do WhatsApp do condomínio.
 * O síndico lê o QR Code uma vez e o sistema passa a enviar as mensagens
 * automaticamente pelo número dele.
 */
export function useWhatsAppConnection() {
  const { profile } = useAuth()
  const condominiumId = profile?.condominium_id || ""
  const queryClient = useQueryClient()
  const [qrCode, setQrCode] = useState<string | null>(null)

  const status = useQuery({
    queryKey: ["whatsapp-status", condominiumId],
    queryFn: () => statusWhatsApp(condominiumId),
    enabled: !!condominiumId,
    // enquanto o QR está na tela, verifica com frequência para detectar a leitura
    refetchInterval: qrCode ? 3000 : 30000,
  })

  const conectado = status.data?.conectado === true

  // assim que conectar, some com o QR e registra no banco
  useEffect(() => {
    if (!conectado) return
    setQrCode(null)
    if (!condominiumId) return
    db
      .from("whatsapp_instances")
      .upsert(
        {
          condominium_id: condominiumId,
          instance_name: `condo-${condominiumId}`,
          status: "conectado",
          connected_at: new Date().toISOString(),
          last_checked_at: new Date().toISOString(),
        },
        { onConflict: "condominium_id" },
      )
      .then(({ error }) => {
        if (error) console.error("Erro ao registrar instância:", error)
      })
  }, [conectado, condominiumId])

  const conectar = useMutation({
    mutationFn: async () => {
      if (!condominiumId) throw new Error("Condomínio não identificado")
      return conectarWhatsApp(condominiumId, profile?.full_name || undefined)
    },
    onSuccess: (dados: WhatsAppConnection) => {
      if (dados.ok === false) {
        toast.error(dados.erro || "Não foi possível iniciar a conexão")
        return
      }
      if (dados.ja_conectado) {
        toast.success("Este número já está conectado!")
        queryClient.invalidateQueries({ queryKey: ["whatsapp-status", condominiumId] })
        return
      }
      setQrCode(dados.qrcode || null)
      toast.success("QR Code gerado — escaneie com o WhatsApp do condomínio")
    },
    onError: (erro: Error) => toast.error(erro.message || "Erro ao conectar o WhatsApp"),
  })

  const desconectar = useMutation({
    mutationFn: async () => {
      if (!condominiumId) throw new Error("Condomínio não identificado")
      return desconectarWhatsApp(condominiumId)
    },
    onSuccess: async () => {
      setQrCode(null)
      if (condominiumId) {
        await db
          .from("whatsapp_instances")
          .update({ status: "desconectado", connected_at: null })
          .eq("condominium_id", condominiumId)
      }
      queryClient.invalidateQueries({ queryKey: ["whatsapp-status", condominiumId] })
      toast.success("WhatsApp desconectado")
    },
    onError: (erro: Error) => toast.error(erro.message || "Erro ao desconectar"),
  })

  return {
    condominiumId,
    conectado,
    estado: status.data?.estado || "desconectado",
    carregando: status.isLoading,
    qrCode,
    limparQrCode: () => setQrCode(null),
    conectar,
    desconectar,
    recarregar: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-status", condominiumId] }),
  }
}

export type PublicoAlvo = "all" | "residents" | "staff"

const PAPEIS_POR_PUBLICO: Record<PublicoAlvo, string[] | null> = {
  all: null, // todo mundo do condomínio
  residents: ["morador"],
  staff: ["porteiro", "sindico", "administracao"],
}

/**
 * Telefones de WhatsApp dos moradores/equipe do condomínio,
 * usados nos envios em massa (avisos e convocações).
 */
export function useDestinatariosWhatsApp() {
  const { profile } = useAuth()
  const condominiumId = profile?.condominium_id || ""

  const buscar = useCallback(
    async (publico: PublicoAlvo = "all"): Promise<string[]> => {
      if (!condominiumId) return []

      const { data, error } = await db
        .from("profiles")
        .select("phone, whatsapp_phone, role, is_active")
        .eq("condominium_id", condominiumId)

      if (error) {
        console.error("Erro ao buscar destinatários:", error)
        return []
      }

      const papeis = PAPEIS_POR_PUBLICO[publico]
      const numeros = (data || [])
        .filter((p: any) => p.is_active !== false)
        .filter((p: any) => !papeis || papeis.includes(p.role))
        .map((p: any) => String(p.whatsapp_phone || p.phone || "").replace(/\D/g, ""))
        .filter((n: string) => n.length >= 10)

      return Array.from(new Set(numeros)) // sem duplicados
    },
    [condominiumId],
  )

  return { buscar, disponivel: !!condominiumId }
}

/**
 * Envio automático de mensagens pelo WhatsApp do condomínio.
 * Já respeita intervalo entre mensagens para não bloquear o número.
 */
export function useEnviarWhatsApp() {
  const { profile, user } = useAuth()
  const condominiumId = profile?.condominium_id || ""

  const enviar = useCallback(
    async (params: { numeros: string[]; texto: string; origem?: string; silencioso?: boolean }) => {
      const numeros = (params.numeros || []).map((n) => String(n || "").replace(/\D/g, "")).filter(Boolean)
      if (!condominiumId || numeros.length === 0 || !params.texto?.trim()) {
        return { ok: false, enviados: 0, falhas: 0, total: 0 }
      }

      try {
        const resultado = await enviarWhatsApp({ condominiumId, numeros, texto: params.texto })

        if (resultado.ok) {
          // histórico para auditoria
          await db.from("whatsapp_messages").insert(
            numeros.map((phone) => ({
              condominium_id: condominiumId,
              phone,
              message: params.texto,
              origem: params.origem || "manual",
              status: "enviado",
              sent_by: user?.id || null,
            })),
          )
          if (!params.silencioso) {
            toast.success(
              resultado.enviados > 1
                ? `${resultado.enviados} mensagens enviadas pelo WhatsApp`
                : "Mensagem enviada pelo WhatsApp",
            )
          }
        } else if (!params.silencioso) {
          toast.error(resultado.erro || "Não foi possível enviar pelo WhatsApp")
        }

        return resultado
      } catch (erro) {
        console.error("Erro no envio de WhatsApp:", erro)
        if (!params.silencioso) toast.error("Erro ao enviar pelo WhatsApp")
        return { ok: false, enviados: 0, falhas: numeros.length, total: numeros.length }
      }
    },
    [condominiumId, user?.id],
  )

  return { enviar, disponivel: !!condominiumId }
}
